import {
  DISCIPLINES,
  DISCIPLINE_LABEL_AR,
  DISCIPLINE_LABEL_EN,
  DISCIPLINE_SWATCH,
  warrantiesForDiscipline,
  type Discipline,
  type Warranties,
  type WarrantyItem,
} from '@alrimal/contracts';
import { useLocale } from '../LocaleContext.tsx';

/**
 * The signature control (§3).
 *
 * The competitor site shows buyers the pipes inside their future walls and
 * never says why. Here the two are one gesture: switching to the piping layer
 * surfaces «السباكة — 15 سنة» in the same motion, because the warranty is
 * driven by the same event that changes the geometry.
 *
 * Each chip also carries its own guarantee inline, so the promise is legible
 * before anyone taps anything — the control advertises what it is for.
 */

export interface LayerBarProps {
  active: readonly Discipline[];
  warranties: Warranties;
  onToggle(discipline: Discipline): void;
}

export function LayerBar({ active, warranties, onToggle }: LayerBarProps) {
  const { t, locale, years } = useLocale();
  const labels = locale === 'ar' ? DISCIPLINE_LABEL_AR : DISCIPLINE_LABEL_EN;

  const shown = DISCIPLINES.filter((discipline) => active.includes(discipline))
    .flatMap((discipline) =>
      warrantiesForDiscipline(warranties, discipline).map((item) => ({
        discipline,
        item,
      })),
    );

  return (
    <div className="layerbar">
      <div className="layerbar__row" role="group" aria-label={t('layer.title')}>
        <span className="layerbar__label">{t('layer.title')}</span>
        {DISCIPLINES.map((discipline) => {
          const items = warrantiesForDiscipline(warranties, discipline);
          const headline = longest(items);
          return (
            <button
              key={discipline}
              type="button"
              className="layer-chip"
              style={{ '--swatch': DISCIPLINE_SWATCH[discipline] } as React.CSSProperties}
              aria-pressed={active.includes(discipline)}
              onClick={() => onToggle(discipline)}
            >
              <span className="layer-chip__swatch" aria-hidden="true" />
              <span>{labels[discipline]}</span>
              {headline ? (
                <span className="layer-chip__years num">
                  {years(headline.years)}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <WarrantyCallout entries={shown} />
    </div>
  );
}

function WarrantyCallout({
  entries,
}: {
  entries: ReadonlyArray<{ discipline: Discipline; item: WarrantyItem }>;
}) {
  const { t, yearsUnit } = useLocale();

  if (entries.length === 0) {
    return (
      <p className="warranty warranty--empty" role="status" aria-live="polite">
        {t('layer.empty')}
      </p>
    );
  }

  return (
    <div className="warranty" role="status" aria-live="polite">
      <ul className="warranty__items">
        {entries.map(({ discipline, item }) => (
          <li className="warranty__item" key={`${discipline}-${item.labelAr}`}>
            <span className="warranty__years num" aria-hidden="true">
              {item.years}
            </span>
            <span className="warranty__unit">{yearsUnit(item.years)}</span>
            <span className="warranty__label">{item.labelAr}</span>
            {item.noteAr ? (
              <span className="warranty__note">{item.noteAr}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function longest(items: readonly WarrantyItem[]): WarrantyItem | undefined {
  return items.reduce<WarrantyItem | undefined>(
    (best, item) => (best === undefined || item.years > best.years ? item : best),
    undefined,
  );
}
